import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Close';
import {
    GridRowsProp,
    GridRowModesModel,
    GridRowModes,
    DataGridPro,
    GridColumns,
    GridRowParams,
    MuiEvent,
    GridToolbarContainer,
    GridActionsCellItem,
    GridEventListener,
    GridRowId,
    GridRowModel,
    useGridApiRef,
    GridApiRef,
} from '@mui/x-data-grid-pro';
import useChats from '../../hooks/useChats';
import SimpleDialog from '../../../common/SimpleDialog';
import IconButton from '@mui/material/IconButton';
import { ChatUtterance } from '../../stores/NodeStore';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { te } from 'date-fns/locale';
import { GridApiPro } from '@mui/x-data-grid-pro/models/gridApiPro';


interface EditToolbarProps {
    setEditing: (value: boolean) => void
    setRows: (newRows: (oldRows: GridRowsProp) => GridRowsProp) => void;
    setRowModesModel: (
        newModel: (oldModel: GridRowModesModel) => GridRowModesModel,
    ) => void;
    apiRef: React.MutableRefObject<GridApiPro>
}

function EditToolbar(props: EditToolbarProps) {
    const { setRows, setRowModesModel, apiRef, setEditing } = props;

    const handleClick = () => {
        const id = crypto.randomUUID();
        setRows((oldRows) => [...oldRows, { id, text: '', isNew: true }]);
        setRowModesModel((oldModel) => ({
            ...oldModel,
            [id]: { mode: GridRowModes.Edit, fieldToFocus: 'text' },
        }));
        setEditing(true)
    };

    return (
        <GridToolbarContainer sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
                Add record
            </Button>
        </GridToolbarContainer>
    );
}

type Props = {
    open: boolean,
    onClose: () => void
}

const UtteranceEditableList = ({ open, onClose }: Props) => {
    const { chat, saveChat, updateChatName, updateChatIntent, updateUtterances, updateUtterance } = useChats();
    const [rows, setRows] = useState(chat.utterances);
    const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>({});
    const apiRef = useGridApiRef();
    const [close, setClose] = useState(false);
    const [editing, setEditing] = useState(false);
    
    const handleDeleteClick = (id: GridRowId) => () => {
        setRows(rows.filter((row) => row.id !== id));
    };

    const processRowUpdate = (newRow: ChatUtterance) => {
        const updatedRow = { ...newRow, isNew: false };
        const temp = structuredClone(rows);

        var rowIndex = temp.findIndex(s => s.id === newRow.id);

        if (rowIndex > -1) {
            temp[rowIndex] = updatedRow;
        }

        setRows(temp);

        if (close) {
            updateUtterances(temp);
            onClose();
        } 

        return updatedRow;
    };

    const handleOkay = () => {
        if (!editing) {
            updateUtterances(rows);
            onClose();
        } else {
            setClose(true);
        }        
    }

    const handleOnEditEnd = () => {
        setEditing(false)       
    }

    const columns: GridColumns = [
        { field: 'text', headerName: 'Utterance', editable: true, flex: 1 },
        {
            flex: 0.2,
            field: '',
            maxWidth: 80,
            align: 'center',
            renderCell: ({ id }) => {
                return (
                    <>
                        <IconButton onClick={handleDeleteClick(id)} title="Delete">
                            <DeleteIcon fontSize='small' />
                        </IconButton>
                    </>
                )
            }
        }
    ];

    return (
        <SimpleDialog open={open} onClose={onClose} sx={{ padding: 5, minHeight: 650, maxHeight: 650 }} size="md" fullHeight={false}>
            <Typography variant='h6'>Edit Utterances</Typography>

            <Box display='flex' flexDirection='column' gap={2}>
                <Box
                    sx={{
                        marginTop: 2,
                        height: 500,
                        width: '100%',
                        '& .actions': {
                            color: 'text.secondary',
                        },
                        '& .textPrimary': {
                            color: 'text.primary',
                        },
                    }}
                >
                    <DataGridPro editMode="row" rows={rows} columns={columns}
                        apiRef={apiRef}
                        disableVirtualization
                        experimentalFeatures={{ newEditingApi: true }}
                        processRowUpdate={processRowUpdate}
                        onRowEditStart={() => setEditing(true)}
                        onRowEditStop={handleOnEditEnd}
                        onProcessRowUpdateError={(e) => {
                            console.log(e);
                        }}
                        components={{
                            Toolbar: EditToolbar,
                        }}
                        componentsProps={{
                            toolbar: { setRows, setRowModesModel, apiRef, setEditing },
                        }}

                        rowModesModel={rowModesModel}
                        onRowModesModelChange={(newModel) => setRowModesModel(newModel)}
                    />
                </Box>
                <Box display='flex' justifyContent='flex-end' gap={2} sx={{ paddingTop: 2 }}>
                    <Button size='small' variant='contained' onClick={() => { setRows(chat.utterances); onClose(); }}>Cancel</Button>
                    <Button size='small' variant='contained' onClick={handleOkay}>Okay</Button>
                </Box>
            </Box>
        </SimpleDialog>
    );
}

export default UtteranceEditableList;